import {Box, Grid, Group, Stack, Switch, Text, TextInput} from '@mantine/core';
import React, {useState} from 'react';

import {FILTER_PREDICATES_BY_MEASUREMENT_TYPE, MeasurementType} from 'samples/MeasurementForm';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInput from 'ui-library/inputs/NumericInput';
import SearchableUnitDropdown from 'units/SearchableUnitDropdown';
import {HydratedUnit} from 'units/util';
import {NumericInputChangeEvent} from 'util/Types';

import PatternAssignmentIntegerInput from '../pattern-controls/PatternAssignmentIntegerInput';
import PatternDirectionDropdown from '../pattern-controls/PatternDirectionDropdown';
import {PatternDirection} from '../PlateMapConstants';

/**
 * Settings component for configuring concentration layers in plate maps.
 *
 * Allows users to:
 * - Set a name for the concentration layer
 * - Define concentration values and units
 * - Optionally define dilution settings including dilution factor, dilution direction, and replicates
 */
export default function ConcentrationLayerSettings(): JSX.Element {
  const [concentrationLayerName, setConcentrationLayerName] = useState('');
  const [concentration, setConcentration] = useState<number | null>(null);
  const [concentrationUnit, setConcentrationUnit] = useState<HydratedUnit | null>(null);
  const [allowSetDilution, setAllowSetDilution] = useState(false);
  const [dilutionFactor, setDilutionFactor] = useState<number | null>(null);
  const [dilutionDirection, setDilutionDirection] = useState<PatternDirection>(PatternDirection.RIGHT);
  const [replicates, setReplicates] = useState<number | null>(null);
  const [replicateDirection, setReplicateDirection] = useState<PatternDirection>(PatternDirection.RIGHT);

  return (
    <Stack spacing="sm">
      <Text size="sm" c="dimmed">
        Select wells in the plate to define concentration values.
      </Text>
      <TextInput
        label="Name"
        labelProps={{fw: 'bold'}}
        autoFocus={true}
        onChange={(e) => {
          setConcentrationLayerName(e.target.value);
        }}
        placeholder="Name"
        value={concentrationLayerName}
      />
      <Box>
        <FieldLabel label="Units" />
        <SearchableUnitDropdown
          selectedId={concentrationUnit?.id}
          unitTypeFilter={FILTER_PREDICATES_BY_MEASUREMENT_TYPE[MeasurementType.CONCENTRATION]}
          displaySelectionAsSymbol={true}
          onSelect={(unit) => {
            setConcentrationUnit(unit);
          }}
        />
      </Box>
      <Box>
        <FieldLabel label="Concentration" />
        <NumericInput
          value={concentration}
          min={0}
          onChange={(e: NumericInputChangeEvent) => {
            setConcentration(e.target.numericValue);
          }}
          isDisabled={false}
          required={true}
          validateInteger={false}
        />
      </Box>
      <Switch
        label="Set dilution"
        styles={{label: {fontWeight: 'bold'}}}
        checked={allowSetDilution}
        onChange={(event) => {
          setAllowSetDilution(!allowSetDilution);
        }}
      />
      {allowSetDilution && (
        <DilutionSettings
          dilutionFactor={dilutionFactor}
          setDilutionFactor={setDilutionFactor}
          dilutionDirection={dilutionDirection}
          setDilutionDirection={setDilutionDirection}
          replicates={replicates}
          setReplicates={setReplicates}
          replicateDirection={replicateDirection}
          setReplicateDirection={setReplicateDirection}
        />
      )}
      <Group ml="auto">
        <Button size={ButtonSize.SMALL} buttonStyle={ButtonStyle.PRIMARY} type="button">
          Apply
        </Button>
      </Group>
    </Stack>
  );
}

interface DilutionSettingsProps {
  dilutionFactor: number | null;
  setDilutionFactor: (value: number | null) => void;
  dilutionDirection: PatternDirection;
  setDilutionDirection: (direction: PatternDirection) => void;
  replicates: number | null;
  setReplicates: (value: number | null) => void;
  replicateDirection: PatternDirection;
  setReplicateDirection: (direction: PatternDirection) => void;
}

function DilutionSettings({
  dilutionFactor,
  setDilutionFactor,
  dilutionDirection,
  setDilutionDirection,
  replicates,
  setReplicates,
  replicateDirection,
  setReplicateDirection,
}: DilutionSettingsProps): JSX.Element {
  return (
    <Box>
      <Grid>
        <Grid.Col span={6}>
          <FieldLabel label="Dilution factor" />
          <NumericInput
            value={dilutionFactor}
            min={0}
            onChange={(e: NumericInputChangeEvent) => {
              setDilutionFactor(e.target.numericValue);
            }}
            isDisabled={false}
            required={false}
            validateInteger={false}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <PatternDirectionDropdown
            label="Dilution direction"
            selectedDirection={dilutionDirection}
            onSelectDirection={(direction) => {
              setDilutionDirection(direction);
            }}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={6}>
          <PatternAssignmentIntegerInput
            label="Replicates"
            value={replicates}
            onChange={(value) => {
              setReplicates(value);
            }}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <PatternDirectionDropdown
            label="Replicate direction"
            selectedDirection={replicateDirection}
            onSelectDirection={(replicateDirection) => {
              setReplicateDirection(replicateDirection);
            }}
          />
        </Grid.Col>
      </Grid>
    </Box>
  );
}
